import {ProductForm} from '@shopify/theme-product-form';

import ModalDialog from './modal-dialog';

class ProductModal extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      optionsToggle: '[data-options-toggle]',
      optionsToggleLabel: '[data-options-toggle-label]',
      optionsWrapper: '[data-product-options-wrapper]',
      option: '[data-product-option]',
      addToCartForm: '[data-product-form]',
      submitButton: '[data-submit-button]',
      productImage: '[data-product-image]',
      helperText: '[data-helper-text]',
    };

    const stringData = this.querySelector('[data-strings-json]');
    this.strings = JSON.parse(stringData.textContent);
    this.product = JSON.parse(this.getAttribute('data-product'));

    this.optionsToggleEl = this.querySelector(this.selectors.optionsToggle);
    this.optionsToggleLabels = this.querySelectorAll(this.selectors.optionsToggleLabel);
    this.optionsWrapperEl = this.querySelector(this.selectors.optionsWrapper);
    this.optionsEl = this.querySelectorAll(this.selectors.option);
    this.addToCartForm = this.querySelector(this.selectors.addToCartForm);
    this.submitButtonEl = this.querySelector(this.selectors.submitButton);
    this.productImage = this.querySelector(this.selectors.productImage);

    this.init();
  }

  init() {
    this.submitButtonEl.addEventListener('click', this.handleFormSubmit.bind(this));
    this.addToCartForm.addEventListener('submit', this.handleFormSubmit.bind(this));

    // Toggle options button : toggleOptions()
    this.optionsToggleEl?.addEventListener('click', this.toggleOptions.bind(this));
    this.productImage?.addEventListener('click', () => {
      this.optionsWrapperEl.setAttribute('aria-expanded', 'false');
      if (this.getAttribute('data-state') === 'notify') {
        this.setAttribute('data-state', 'add');
      }
    });

    // Product options : handleOptionSelection()
    this.optionsEl?.forEach((option) => option.addEventListener('click', this.handleOptionSelection.bind(this)));

    // Product form
    const options = {
      onFormSubmit: this.onFormSubmit.bind(this),
    };

    this.productForm = new ProductForm(this.addToCartForm, this.product, options);
  }

  onFormSubmit(event) {
    event.preventDefault();
    // Show select size error if no size chosen
    const variantInput = this.addToCartForm?.elements.id;
    const variant = variantInput?.value;

    const propertyNameRegex = /(?:^(properties\[))(.*?)(?:\])/;
    const propertyInputs = this.querySelectorAll('[name^="properties"]');
    const properties = Array.from(propertyInputs).map((property) => {
      const name = propertyNameRegex.exec(property.name)[2];
      const value = property.value;
      return [name, value];
    });

    if (variant) {
      window.dispatchEvent(new CustomEvent('close_modal:product', {}));

      const item = {
        id: parseInt(variant, 10),
        quantity: 1,
        properties: Object.fromEntries(properties),
      };
      window.sideCart.handleAddToCartEvent(item, true);
    }
  }

  // TOGGLE OPTIONS VISIBILITY | Show or hide product options dropdown when clicked
  toggleOptions(event) {
    event?.preventDefault();

    const isExpanded = this.optionsWrapperEl.getAttribute('aria-expanded') === 'true';
    this.optionsWrapperEl.setAttribute('aria-expanded', !isExpanded);
  }

  // Update product options on click
  handleOptionSelection(event) {
    this.toggleOptions();

    // Set state
    const option = event.currentTarget;
    const state = option.dataset.productOption;
    this.setAttribute('data-state', state);

    this.updateSubmitButton(state);
  }

  // Update submit button on state change
  updateSubmitButton(state) {
    switch (state) {
      case 'add':
        this.submitButtonEl.classList.remove('btn--tertiary');
        this.submitButtonEl.innerHTML = this.strings.addToCart;
        this.submitButtonEl.disabled = false;
        break;

      default:
        this.submitButtonEl.classList.add('btn--tertiary');
        this.submitButtonEl.innerHTML = this.strings.optionSelectPrompt;
        this.submitButtonEl.disabled = false;
        this.optionsToggleLabels.forEach((label) => {
          label.innerHTML = this.strings.optionSelectPrompt;
        });
    }
  }

  handleFormSubmit(event) {
    const state = this.getAttribute('data-state');

    switch (state) {
      case 'add':
        break;

      case 'unavailible':
        event.preventDefault();
        this.setAttribute('data-state', 'out_of_stock');
        break;

      default:
        event.preventDefault();
    }
  }
}

const selectors = {
  modal: '#modal-product',
  modalContent: '[data-product-modal]',
  modalTriggers: '[data-product-modal-trigger]',
};

const classes = {
  placeholderContent: 'product-modal--placeholder',
};

export default function initProductModal() {
  const productModalEl = document.querySelector(selectors.modal);
  const productModalTriggers = document.querySelectorAll(selectors.modalTriggers);
  const modalContent = productModalEl.querySelector(selectors.modalContent);

  const placeholderContent = `
  <div class="product-modal__image">
    <div class="component-image__wrapper aspect-ratio aspect-ratio--2-3"></div>
  </div>

  <div>
    <div class="product-modal__options-wrapper"></div>
    <div class="product-modal__footer">
      <div class="btn btn--disabled" style="opacity: 0;">&nbsp;</div>
    </div>
  </div>
  `;

  const updateModalContent = async (trigger) => {
    const handle = trigger.dataset.productModalTrigger;
    const rootUrl = window.theme.locale?.primary ? '' : window.theme.locale.rootUrl;
    const url = `${rootUrl}/products/${handle}?view=modal`;

    const response = await fetch(url);
    if (response.ok) {
      modalContent.innerHTML = await response.text();
      modalContent.classList.remove(classes.placeholderContent);
    }
  };

  if (productModalEl && productModalTriggers.length > 0) {
    const productModalFocusEl = productModalEl.querySelector('[data-add-to-cart]');

    window.productModal = new ModalDialog(
      productModalEl,
      {
        triggerEl: productModalTriggers,
        focusEl: productModalFocusEl ?? false,
        modalId: 'product',
      },
      updateModalContent
    );
  }

  if (!customElements.get('product-modal')) {
    customElements.define('product-modal', ProductModal);
    modalContent.innerHTML = placeholderContent;
  }

  // Replace content when modal is closed
  window.addEventListener('product_modal_closed', () => {
    modalContent.innerHTML = placeholderContent;
    modalContent.classList.add(classes.placeholderContent);
  });
}
